import { OperatingSystem } from '@studio/react-utils/user-agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import deepFreeze from 'deep-freeze';
import type { Action, Combo, KeyCode } from './types.js';
import { Category, Modifier } from './types.js';
import { Command } from '#~/command/index.js';

/** @internal */
const __modifiers = {
  glyphs: {
    [Modifier.Command]: 'Ctrl',
    [Modifier.Control]: 'Ctrl',
    [Modifier.Option]: 'Alt',
    [Modifier.Shift]: 'Shift'
  },
  order: [
    // Command + Control are treated interchangeably.  A shortcut will never have both.
    Modifier.Control,
    Modifier.Command,
    Modifier.Option,
    Modifier.Shift
  ],
  separator: '+'
};

/** Hot key categories. */
export const Categories = deepFreeze({
  [Category.Control]: {
    actions: [
      Command.PTZ_ZOOM_IN,
      Command.PTZ_ZOOM_OUT,
      Command.PTZ_TILT_UP,
      Command.PTZ_TILT_DOWN,
      Command.PTZ_PAN_LEFT,
      Command.PTZ_PAN_RIGHT,
      Command.FOCUS_NEAR,
      Command.FOCUS_FAR,
      Command.FOCUS_ONE_PUSH,
      Command.FOCUS_MODE,
      Command.PACE_ZOOM_UP,
      Command.PACE_ZOOM_DOWN,
      Command.PACE_PT_UP,
      Command.PACE_PT_DOWN,
      Command.CONTROL_PRESET_ADD,
      Command.CONTROL_GROUP_NEXT,
      Command.CONTROL_GROUP_PREV,
      Command.CONTROL_PRESET_01,
      Command.CONTROL_PRESET_02,
      Command.CONTROL_PRESET_03,
      Command.CONTROL_PRESET_04,
      Command.CONTROL_PRESET_05,
      Command.CONTROL_PRESET_06,
      Command.CONTROL_PRESET_07,
      Command.CONTROL_PRESET_08,
      Command.CONTROL_PRESET_09,
      Command.CONTROL_PRESET_10,
      Command.PTZ_ZOOM_MIN,
      Command.PTZ_ZOOM_MAX,
      Command.PTZ_TILT_MIN,
      Command.PTZ_TILT_MAX,
      Command.PTZ_PAN_MIN,
      Command.PTZ_PAN_MAX
    ],
    label: 'Camera Controls',
    order: 1
  },
  [Category.Essentials]: {
    actions: [
      Command.TOOL_CENTER,
      Command.TOOL_CINE_CENTER,
      Command.TOOL_FAST_FRAME,
      Command.TOOL_CINE_FRAME,
      Command.TOOL_JOYSTICK,
      Command.TOOL_JOYSTICK_TRIGGER,
      Command.TOOL_ZOOM,
      Command.TOOL_TRACKING,
      Command.PTZ_ZOOM_IN,
      Command.PTZ_ZOOM_OUT,
      Command.CONTROL_PRESET_ADD,
      Command.CONTROL_GROUP_NEXT,
      Command.CONTROL_GROUP_PREV,
      Command.FOCUS_ONE_PUSH,
      Command.FOCUS_NEAR,
      Command.FOCUS_FAR,
      Command.VIDEO_SOURCE_NEXT,
      Command.VIDEO_SOURCE_PREV
    ],
    label: 'Essentials',
    order: 0
  },
  [Category.Sources]: {
    actions: [
      Command.VIDEO_ARRANGEMENT,
      Command.VIDEO_SOURCE_NEXT,
      Command.VIDEO_SOURCE_PREV,
      Command.VIDEO_SOURCE_01,
      Command.VIDEO_SOURCE_02,
      Command.VIDEO_SOURCE_03,
      Command.VIDEO_SOURCE_04,
      Command.VIDEO_SOURCE_05,
      Command.VIDEO_SOURCE_06,
      Command.VIDEO_SOURCE_07,
      Command.VIDEO_SOURCE_08,
      Command.VIDEO_SOURCE_09,
      Command.VIDEO_SOURCE_10
    ],
    label: 'Sources',
    order: 3
  },
  [Category.Toolbar]: {
    actions: [
      Command.TOOL_CENTER,
      Command.TOOL_FAST_FRAME,
      Command.TOOL_JOYSTICK,
      Command.TOOL_JOYSTICK_TRIGGER,
      Command.TOOL_ZOOM,
      Command.TOOL_TRACKING
    ],
    label: 'Toolbar',
    order: 2
  },
  [Category.Miscellaneous]: {
    actions: [Command.TRAY_BOTTOM_TAB, Command.TRAY_LEFT_TAB, Command.TRAY_RIGHT_TAB],
    label: 'Miscellaneous',
    order: 4
  }
} satisfies Record<Category, { actions: Action[] | true; label: string; order: number }>);

/**
 * Default hot keys.
 *
 * @internal
 * @see https://mzl.la/3k4p0ZR
 */
export const DefaultHotKeys = Object.freeze({
  [Command.CONTROL_GROUP_NEXT]: { keyCode: 222, modifiers: 0 },
  [Command.CONTROL_GROUP_PREV]: { keyCode: 186, modifiers: 0 },
  [Command.CONTROL_PRESET_01]: { keyCode: 49, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_02]: { keyCode: 50, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_03]: { keyCode: 51, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_04]: { keyCode: 52, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_05]: { keyCode: 53, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_06]: { keyCode: 54, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_07]: { keyCode: 55, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_08]: { keyCode: 56, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_09]: { keyCode: 57, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_10]: { keyCode: 48, modifiers: Modifier.Shift },
  [Command.CONTROL_PRESET_ADD]: { keyCode: 80, modifiers: Modifier.Command },
  [Command.FOCUS_FAR]: { keyCode: 221, modifiers: 0 },
  [Command.FOCUS_MODE]: { keyCode: 70, modifiers: Modifier.Option },
  [Command.FOCUS_NEAR]: { keyCode: 219, modifiers: 0 },
  [Command.FOCUS_ONE_PUSH]: { keyCode: 70, modifiers: Modifier.Command },
  [Command.PACE_PT_DOWN]: { keyCode: 37, modifiers: Modifier.Shift },
  [Command.PACE_PT_UP]: { keyCode: 39, modifiers: Modifier.Shift },
  [Command.PACE_ZOOM_DOWN]: { keyCode: 189, modifiers: Modifier.Shift },
  [Command.PACE_ZOOM_UP]: { keyCode: 187, modifiers: Modifier.Shift },
  [Command.PTZ_PAN_LEFT]: { keyCode: 37, modifiers: 0 },
  [Command.PTZ_PAN_MAX]: { keyCode: 39, modifiers: Modifier.Option },
  [Command.PTZ_PAN_MIN]: { keyCode: 37, modifiers: Modifier.Option },
  [Command.PTZ_PAN_RIGHT]: { keyCode: 39, modifiers: 0 },
  [Command.PTZ_TILT_DOWN]: { keyCode: 40, modifiers: 0 },
  [Command.PTZ_TILT_MAX]: { keyCode: 38, modifiers: Modifier.Option },
  [Command.PTZ_TILT_MIN]: { keyCode: 40, modifiers: Modifier.Option },
  [Command.PTZ_TILT_UP]: { keyCode: 38, modifiers: 0 },
  [Command.PTZ_ZOOM_IN]: { keyCode: 187, modifiers: 0 },
  [Command.PTZ_ZOOM_MAX]: { keyCode: 187, modifiers: Modifier.Option },
  [Command.PTZ_ZOOM_MIN]: { keyCode: 189, modifiers: Modifier.Option },
  [Command.PTZ_ZOOM_OUT]: { keyCode: 189, modifiers: 0 },
  [Command.TRAY_BOTTOM_TAB]: { keyCode: 190, modifiers: 0 },
  [Command.TRAY_LEFT_TAB]: { keyCode: 188, modifiers: 0 },
  [Command.TRAY_RIGHT_TAB]: { keyCode: 191, modifiers: 0 },
  [Command.TOOL_CENTER]: { keyCode: 67, modifiers: 0 },
  [Command.TOOL_CINE_CENTER]: { keyCode: 67, modifiers: Modifier.Shift },
  [Command.TOOL_FAST_FRAME]: { keyCode: 70, modifiers: 0 },
  [Command.TOOL_CINE_FRAME]: { keyCode: 70, modifiers: Modifier.Shift },
  [Command.TOOL_JOYSTICK_TRIGGER]: { keyCode: 32, modifiers: 0 },
  [Command.TOOL_JOYSTICK]: { keyCode: 74, modifiers: 0 },
  [Command.TOOL_TRACKING]: { keyCode: 65, modifiers: 0 },
  [Command.TOOL_ZOOM]: { keyCode: 90, modifiers: 0 },
  [Command.VIDEO_ARRANGEMENT]: { keyCode: 192, modifiers: 0 },
  [Command.VIDEO_SOURCE_01]: { keyCode: 49, modifiers: 0 },
  [Command.VIDEO_SOURCE_02]: { keyCode: 50, modifiers: 0 },
  [Command.VIDEO_SOURCE_03]: { keyCode: 51, modifiers: 0 },
  [Command.VIDEO_SOURCE_04]: { keyCode: 52, modifiers: 0 },
  [Command.VIDEO_SOURCE_05]: { keyCode: 53, modifiers: 0 },
  [Command.VIDEO_SOURCE_06]: { keyCode: 54, modifiers: 0 },
  [Command.VIDEO_SOURCE_07]: { keyCode: 55, modifiers: 0 },
  [Command.VIDEO_SOURCE_08]: { keyCode: 56, modifiers: 0 },
  [Command.VIDEO_SOURCE_09]: { keyCode: 57, modifiers: 0 },
  [Command.VIDEO_SOURCE_10]: { keyCode: 48, modifiers: 0 },
  [Command.VIDEO_SOURCE_NEXT]: { keyCode: 69, modifiers: 0 },
  [Command.VIDEO_SOURCE_PREV]: { keyCode: 81, modifiers: 0 }
} satisfies Record<Action, Exclude<Combo, false>>);

/**
 * `Event.keyCode` -> glyph mapping.
 *
 * @see https://bit.ly/40Q5jo9
 */
export const Glyphs: Record<KeyCode, string> = Object.freeze({
  // Special
  32: 'Space',

  // Arrows
  37: '←',
  38: '↑',
  39: '→',
  40: '↓',

  // Numbers
  48: '0',
  49: '1',
  50: '2',
  51: '3',
  52: '4',
  53: '5',
  54: '6',
  55: '7',
  56: '8',
  57: '9',

  // Letters
  65: 'A',
  66: 'B',
  67: 'C',
  68: 'D',
  69: 'E',
  70: 'F',
  71: 'G',
  72: 'H',
  73: 'I',
  74: 'J',
  75: 'K',
  76: 'L',
  77: 'M',
  78: 'N',
  79: 'O',
  80: 'P',
  81: 'Q',
  82: 'R',
  83: 'S',
  84: 'T',
  85: 'U',
  86: 'V',
  87: 'W',
  88: 'X',
  89: 'Y',
  90: 'Z',

  // Symbols
  186: ';',
  187: '=',
  188: ',',
  189: '-',
  190: '.',
  191: '/',
  192: '`',
  219: '[',
  220: '\\',
  221: ']',
  222: "'",

  // Firefox oddities.
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  59: ';',
  61: '=',
  173: '-'
});

/** Hot-key Icons. */
export const Icons: { [key in Command]?: T_Maybe<string> } = Object.freeze({
  [Command.TOOL_CENTER]: 'click-center',
  [Command.TOOL_CINE_CENTER]: 'tracking',
  [Command.TOOL_FAST_FRAME]: 'fast-frame',
  [Command.TOOL_CINE_FRAME]: 'cine-frame',
  [Command.TOOL_JOYSTICK]: 'joystick',
  [Command.TOOL_TRACKING]: 'auto-tracking',
  [Command.TOOL_ZOOM]: 'zoom-in'
});

/** Modifier data. */
export const Modifiers = deepFreeze({
  [OperatingSystem.macOS]: {
    glyphs: {
      [Modifier.Command]: '⌘',
      [Modifier.Control]: '⌃',
      [Modifier.Option]: '⌥',
      [Modifier.Shift]: 'Shift' // '⇧'
    },
    order:
      /**
       * MacOS: Control+Option+Shift+Command
       *
       * @see https://bit.ly/3GBHpon
       */
      [Modifier.Control, Modifier.Option, Modifier.Shift, Modifier.Command],
    separator: ''
  },

  [OperatingSystem.Windows]: __modifiers,
  [OperatingSystem.Linux]: __modifiers
} satisfies Record<
  OperatingSystem,
  { glyphs: Record<Modifier, string>; order: Modifier[]; separator: string }
>);

/** @internal */
export const PrefKey = 'hotkeys';

/** @internal */
export const ReservedTags = ':is(input, textarea, select)';

/** @internal Modal surfaces that own the keyboard while they are open. */
export const ModalScope = ':is(dialog[open], [role="dialog"], [role="alertdialog"])';

/** @internal Radix popovers are also `role="dialog"` but do not block the room. */
export const PopperScope = '[data-radix-popper-content-wrapper]';
